import { useEffect, useState } from 'react'

import { City, useCities, useLocationStore } from '@entities/Location'

interface GeolocationPosition {
  latitude: number
  longitude: number
}

interface UseGeolocationCityResult {
  suggestedCity: City | null
  detectedCityName: string | null // City name from geolocation (even if not in list)
  loading: boolean
  error: string | null
}

/**
 * Hook to get city suggestion based on browser geolocation
 * Uses reverse geocoding to get city name from coordinates
 */
export const useGeolocationCity = (): UseGeolocationCityResult => {
  const [ position, setPosition ] = useState<GeolocationPosition | null>(null)
  const [ cityName, setCityName ] = useState<string | null>(null)
  const [ loading, setLoading ] = useState(true)
  const [ error, setError ] = useState<string | null>(null)

  const { cities, loading: citiesLoading } = useCities()
  const { currentCity } = useLocationStore()

  // Skip geolocation if city is already selected
  const shouldSkipGeolocation = !!currentCity

  // Get browser geolocation
  useEffect(() => {
    // Skip if city is already selected
    if (shouldSkipGeolocation) {
      setLoading(false)
      return
    }

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser')
      setLoading(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (geoPosition) => {
        setPosition({
          latitude: geoPosition.coords.latitude,
          longitude: geoPosition.coords.longitude,
        })
        setLoading(false)
      },
      (geoError) => {
        setError(geoError.message)
        setLoading(false)
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      },
    )
  }, [ shouldSkipGeolocation ])

  // Reverse geocoding to get city name from coordinates
  useEffect(() => {
    // Skip if city is already selected
    if (shouldSkipGeolocation) return

    if (!position || cityName) return

    const reverseGeocode = async () => {
      setLoading(true)
      try {
        // Using OpenStreetMap Nominatim API for reverse geocoding
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.latitude}&lon=${position.longitude}&addressdetails=1`,
          {
            headers: {
              'User-Agent': 'MosaicaMarket/1.0',
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to reverse geocode')
        }

        const data = await response.json()
        const city = data.address?.city || data.address?.town || data.address?.municipality || data.address?.county

        if (city) {
          setCityName(city)
        }
      }
      catch (err) {
        console.error('Reverse geocoding error:', err)
        // Silently fail - we'll just not suggest a city
      }
      finally {
        setLoading(false)
      }
    }

    reverseGeocode()
  }, [ position, cityName, shouldSkipGeolocation ])

  // Match city name with available cities
  // Try multiple matching strategies:
  // 1. Exact match (case-insensitive)
  // 2. City name contains geocoded city name
  // 3. Geocoded city name contains city name
  // 4. Normalize both (remove extra spaces, special chars)
  const suggestedCity = cityName && !citiesLoading
    ? cities.find((city) => {
      const cityNameLower = cityName.toLowerCase().trim()
      const availableCityNameLower = city.name.toLowerCase().trim()

      // Exact match
      if (availableCityNameLower === cityNameLower) return true

      // Contains match
      if (availableCityNameLower.includes(cityNameLower)) return true
      if (cityNameLower.includes(availableCityNameLower)) return true

      // Normalize and compare (remove common suffixes/prefixes)
      const normalize = (str: string) => str
        .replace(/^г\.?\s*/i, '') // Remove "г." or "г. " prefix
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim()

      const normalizedCityName = normalize(cityNameLower)
      const normalizedAvailableCity = normalize(availableCityNameLower)

      if (normalizedAvailableCity === normalizedCityName) return true
      if (normalizedAvailableCity.includes(normalizedCityName)) return true
      if (normalizedCityName.includes(normalizedAvailableCity)) return true

      return false
    }) || null
    : null

  // Debug logging
  useEffect(() => {
    console.log('useGeolocationCity Debug:', {
      position,
      cityName,
      citiesCount: cities.length,
      citiesLoading,
      loading,
      suggestedCity: suggestedCity?.name,
      error,
      allCities: cities.map(c => c.name),
      matchingAttempt: cityName ? `Trying to match "${cityName}" with: ${cities.map(c => c.name).join(', ')}` : null,
    })
  }, [ position, cityName, cities, citiesLoading, loading, suggestedCity, error ])

  return {
    suggestedCity,
    detectedCityName: cityName, // Return detected city name even if not matched
    loading: loading || citiesLoading,
    error,
  }
}
